"use client";

import Image from "next/image";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Crown, Users } from "lucide-react";

import { getRiotRankImageUrl } from "@/shared/lib/rank-image";
import { displayRank, rankTierName } from "@/shared/lib/lol-rank";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import { MEDALS, medalMix, medalOf } from "@/shared/lib/medal";
import { rankTierBackground } from "./lib/rank-tier-color";
import {
  TeamBreakdownDialog,
  type TeamBreakdown,
} from "./team-breakdown-dialog";
import { scoreColor } from "@/shared/lib/score-color";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";

/**
 * KAN-808: составы всех команд турнира лентой карточек (эпик KAN-804).
 *
 * Прежний вид — сетка карточек — на турнире в восемь-шестнадцать команд превращался
 * в километр прокрутки ровно между двумя блоками, которые читают чаще всего:
 * пьедесталом и таблицей игроков. Здесь одна карточка в фокусе, соседние поджаты,
 * листание стрелками, точками и клавишами.
 *
 * ТРЕК ДВИГАЕТСЯ TRANSFORM'ОМ, А НЕ SCROLL-SNAP. Причина не в красоте: при
 * перерендере карточек (а он случается на каждый выбор игрока) браузер сбрасывал
 * программный скролл, и подсвеченная точка расходилась с видимой карточкой. С
 * трансформом позиция — производная состояния, расходиться нечему.
 */

/** Порядок ролей — тот же, что на драфте: Топ → Лес → Мид → Стрелок → Поддержка. */
const ROLE_ORDER = ["Top", "Jungle", "Mid", "Adc", "Support"] as const;
const ROLE_ORDER_INDEX: Record<string, number> = Object.fromEntries(
  ROLE_ORDER.map((r, i) => [r, i]),
);

const PLACEMENT_KEY: Record<number, string> = {
  1: "place1",
  2: "place2",
  3: "place3",
};

/**
 * Геометрия стопки — числа из макета, не подобранные заново.
 *
 * Соседи не уезжают за край, а ложатся ПОД активную карточку: наружу торчит только
 * край. Так видно, что турнир больше одной команды, ещё до того, как читатель нашёл
 * стрелки. Дальше второго соседа карточки не рисуются вовсе — третий слой уже
 * неразличим, а стоит столько же, сколько первый.
 */
const STACK_VISIBLE_DEPTH = 2;

/** Сдвиг соседа, пиксели. В макете именно пиксели, а не доля ширины: на узком
 *  экране процент схлопывал бы веер в стопку без просвета. */
const STACK_SHIFT_PX = 106;

/** Приглушение соседа. Одинаковое на любой глубине — важен факт «не в фокусе». */
const STACK_NEIGHBOUR_OPACITY = 0.62;

/** Размытие на слой. Соседи размываются сильно: иначе из-под карточки лезут
 *  обрывки чужого текста и читаются как часть активной. */
const STACK_BLUR_PER_DEPTH = 2.5;

/**
 * Асимметричный кроссфейд: входящая карточка проявляется быстро и без задержки,
 * уходящая гаснет медленно. При одинаковых длительностях в середине перехода обе
 * оказываются полупрозрачными, и получается провал.
 */
const STACK_MOVE = "transform .42s cubic-bezier(.32,.72,0,1)";
const TRANSITION_ACTIVE =
  `${STACK_MOVE}, opacity .16s ease-out, filter .24s ease-out, border-color .3s ease`;
const TRANSITION_NEIGHBOUR =
  `${STACK_MOVE}, opacity .5s ease-in .06s, filter .5s ease-in, border-color .3s ease`;

/**
 * Высоту стопки задаёт АКТИВНАЯ карточка: она одна остаётся в потоке, соседи
 * позиционируются абсолютно поверх неё.
 *
 * Макет задавал контейнеру фиксированные 356px под состав из пяти. Это угадывание
 * вёрстки числом: состав из шести (замена по ходу турнира оставляет в снимке
 * лишнего) там обрезался бы `overflow-hidden`. Считать высоту арифметикой — та же
 * ошибка, только в коде. Рисовать невидимый образец самой высокой карточки — честно
 * по высоте, но дублирует всё её содержимое в DOM, включая имена игроков.
 *
 * Цена решения: сосед с более длинным составом обрежется снизу. Он приглушён,
 * размыт и наполовину перекрыт, а стоит его сфокусировать — виден целиком.
 */

export type RailRosterMember = {
  userId?: string | null;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
  /** Ранг на момент заявки — замороженный, как и всё остальное в снимке. */
  rank?: string | null;
  /**
   * KAN-808: твич-логин НА МОМЕНТ ТУРНИРА. Факт «этот игрок стримил этот турнир».
   * Ссылкой не является: после ренейма канала адрес ведёт в никуда.
   */
  twitchLogin?: string | null;
  /**
   * KAN-808: текущий твич-логин из профиля. Кликабелен только он. null при живом
   * `twitchLogin` означает «тогда стримил, сейчас канал отвязан» — значок остаётся,
   * но неактивным: это правда о турнире, а не поломка витрины.
   */
  twitchLoginCurrent?: string | null;
};

export type RailTeam = {
  originalTeamId: string;
  name: string;
  tag?: string | null;
  icon?: string | null;
  captainId?: string | null;
  placement?: number | null;
  roster?: RailRosterMember[] | null;
  stats?: { wins: number; losses: number; winrate: number } | null;
  /**
   * Средний балл состава. Приходит СНАРУЖИ и только оттуда: считать его на клиенте
   * запрещено решением эпика KAN-757 — балл один на весь проект (GL Score, KAN-764),
   * а ранжирование команд по нему живёт в KAN-783. Пока тот не сделан, проп пуст и
   * строка не рисуется — выдуманное среднее хуже отсутствующего.
   */
  avgScore?: number | null;
};

function memberName(m: RailRosterMember): string {
  return String(m.displayName ?? m.username ?? "").trim();
}

/** Иконка Twitch: в lucide её нет, а тянуть пакет ради одного глифа незачем. */
function TwitchGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M4 3h16v11l-4 4h-3l-2.5 2.5H9V18H4V3zm2.5 2v10.5H9v2l2-2h3.5l2.5-2.5V5H6.5zm4.5 2.5h1.8v5H11v-5zm4 0h1.8v5H15v-5z" />
    </svg>
  );
}

/**
 * Значок стрима. Три состояния, и их важно не схлопнуть:
 *  - канал привязан сейчас → ссылка на живой канал;
 *  - стримил тогда, сейчас отвязан → значок без ссылки с подписью-объяснением;
 *  - не стримил → ничего.
 */
function TwitchMark({ member }: { member: RailRosterMember }) {
  const tUi = useTranslations("Ui");
  const live = member.twitchLoginCurrent?.trim();
  const frozen = member.twitchLogin?.trim();

  if (live) {
    return (
      <a
        href={`https://twitch.tv/${live}`}
        target="_blank"
        rel="noopener noreferrer"
        title={tUi("litTeamRosterRail.smotretNaTwitch", { name: live })}
        aria-label={tUi("litTeamRosterRail.kanalNaTwitch", { name: live })}
        className="flex-none text-[#a970ff] transition-opacity hover:opacity-80"
        onClick={(e) => e.stopPropagation()}
      >
        <TwitchGlyph className="h-[15px] w-[15px]" />
      </a>
    );
  }

  if (frozen) {
    return (
      <span
        title={tUi("litTeamRosterRail.strimilPodNikom", { name: frozen })}
        aria-label={tUi("litTeamRosterRail.strimilKanalNePrivyazan")}
        className="flex-none text-muted-foreground/50"
      >
        <TwitchGlyph className="h-[15px] w-[15px]" />
      </span>
    );
  }

  return null;
}

/**
 * Мини-крест ранга на подложке в цвет тира. Неизвестный ранг — пусто, а не серая
 * заглушка «Unranked»: заглушка читается как ещё один тир.
 *
 * Крест картинкой, а не чипом с текстом: текстовый ранг в строке выглядел бы как
 * очередная метрика рядом с ником.
 */
function RankMark({ rank }: { rank?: string | null }) {
  const resolved = displayRank(rank ?? undefined);
  const src = getRiotRankImageUrl(resolved);
  if (!src) return null;

  const tierName = rankTierName(resolved);

  return (
    <span
      className="flex h-[26px] w-[34px] flex-none items-center justify-center rounded-[7px]"
      style={{ background: rankTierBackground(resolved) ?? undefined }}
      title={tierName}
    >
      <Image src={src} alt={tierName} width={22} height={22} unoptimized />
    </span>
  );
}

function RosterRow({
  member,
  isCaptain,
  onSelect,
}: {
  member: RailRosterMember;
  isCaptain: boolean;
  onSelect?: (userId: string) => void;
}) {
  const tPodium = useTranslations("TournamentStats.podium");
  const name = memberName(member) || tPodium("unknownPlayer");
  const selectable = Boolean(member.userId && onSelect);

  // Кликабельная строка — настоящая кнопка (Enter/Space из коробки), иначе — просто строка.
  const Row = selectable ? "button" : "div";

  return (
    <Row
      type={selectable ? "button" : undefined}
      onClick={selectable ? () => onSelect!(member.userId!) : undefined}
      className={cn(
        "flex items-center gap-[11px] rounded-[11px] bg-muted/40 px-3 py-[9px]",
        selectable &&
          "w-full text-left cursor-pointer transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <span className="flex w-5 flex-none items-center justify-center text-muted-foreground">
        <RoleIcon role={member.role} size={17} />
      </span>
      {/* Макет рисует кружок с инициалом. Аватар оставлен намеренно: у нас он есть,
          и все соседние витрины подписывают игрока лицом, а не буквой. Инициал
          остаётся фолбэком — ровно то, что в макете. */}
      <Avatar className="h-[26px] w-[26px] flex-none border border-border/60">
        <AvatarImage src={member.avatarUrl ?? undefined} alt={name} />
        <AvatarFallback className="bg-muted text-[11px] font-bold text-muted-foreground">
          {name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-foreground">
        {name}
      </span>
      {isCaptain ? (
        <Crown
          className="h-4 w-4 flex-none"
          style={{ color: MEDALS.gold.mid }}
          aria-label={tPodium("captain")}
        />
      ) : null}
      <TwitchMark member={member} />
      <RankMark rank={member.rank} />
    </Row>
  );
}

function TeamCard({
  team,
  onSelectPlayer,
  onOpenBreakdown,
}: {
  team: RailTeam;
  onSelectPlayer?: (userId: string) => void;
  onOpenBreakdown?: () => void;
}) {
  const t = useTranslations("TournamentStats.rosterRail");
  const tPodium = useTranslations("TournamentStats.podium");

  const roster = useMemo(
    () =>
      (team.roster ?? [])
        .slice()
        .sort(
          (a, b) =>
            (ROLE_ORDER_INDEX[a.role ?? ""] ?? 99) -
            (ROLE_ORDER_INDEX[b.role ?? ""] ?? 99),
        ),
    [team.roster],
  );

  const placementKey = team.placement ? PLACEMENT_KEY[team.placement] : undefined;
  const placement = placementKey ? tPodium(placementKey) : null;
  const medal = medalOf(team.placement);
  // Герб макета несёт ТЕГ, а не имя: три буквы тега — то, чем команду зовут в чате.
  const crestText = (team.tag ?? team.name).slice(0, 3).toUpperCase();

  return (
    <Card
      className="h-full bg-card"
      style={{
        // Призовая рамка тонирована металлом, остальные — обычный бордер. Место
        // видно раньше, чем читатель добрался до бейджа справа.
        borderColor: medalMix(medal, 45, "var(--border)"),
        borderRadius: 18,
      }}
    >
      <CardContent className="flex h-full flex-col gap-3.5 p-5">
        <div className="flex items-center gap-3.5">
          <Avatar
            className="h-[52px] w-[52px] flex-none rounded-[15px]"
            style={{ background: medalMix(medal, 22, "var(--muted)") }}
          >
            <AvatarImage src={team.icon ?? undefined} alt={team.name} />
            <AvatarFallback
              className="rounded-[15px] bg-transparent font-heading text-[19px] font-bold"
              style={{ color: medal ? medal.mid : "var(--muted-foreground)" }}
            >
              {crestText || <Users className="h-5 w-5 text-muted-foreground" />}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-[9px]">
              {/* KAN-900: имя команды открывает разбор — чипы стиля, метрики и
                  связки. Кнопка именно на имени, а не на всей карточке: строки
                  состава внутри уже кликаются и выбирают игрока, и общий обработчик
                  на карточке отбирал бы у них клик. */}
              {onOpenBreakdown ? (
                <button
                  type="button"
                  onClick={onOpenBreakdown}
                  className="min-w-0 truncate text-left font-heading text-[27px] font-bold leading-[1.25] tracking-[-0.01em] text-foreground underline decoration-transparent decoration-2 underline-offset-4 transition-colors hover:decoration-primary"
                >
                  {team.name}
                </button>
              ) : (
                <span className="truncate font-heading text-[27px] font-bold leading-[1.25] tracking-[-0.01em] text-foreground">
                  {team.name}
                </span>
              )}
              {team.tag ? (
                <span className="flex-none rounded-md bg-muted px-[7px] py-[3px] text-[10px] font-bold tracking-[0.06em] text-muted-foreground">
                  {team.tag}
                </span>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-muted-foreground">
              {team.stats ? (
                <span className="whitespace-nowrap text-sm tabular-nums">
                  {team.stats.wins} : {team.stats.losses}{" "}
                  <span className="text-[10px] font-bold uppercase tracking-[0.09em]">
                    {tPodium("maps")}
                  </span>
                </span>
              ) : null}
              {typeof team.avgScore === "number" ? (
                <span className="whitespace-nowrap text-xs">
                  {tPodium("averageScore")}{" "}
                  <span
                    className="font-bold tabular-nums"
                    style={{ color: scoreColor(team.avgScore) }}
                  >
                    {Math.round(team.avgScore)}
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          {placement ? (
            <span
              className="flex-none rounded-lg px-[11px] py-1.5 text-[10px] font-bold uppercase tracking-[0.09em]"
              style={{
                background: medal
                  ? medalMix(medal, 18, "transparent")
                  : "var(--muted)",
                color: medal ? medal.mid : "var(--muted-foreground)",
              }}
            >
              {placement}
            </span>
          ) : null}
        </div>

        {roster.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {roster.map((m, idx) => (
              <RosterRow
                key={m.userId ?? `${team.originalTeamId}-${idx}`}
                member={m}
                isCaptain={Boolean(m.userId && m.userId === team.captainId)}
                onSelect={onSelectPlayer}
              />
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("noRoster")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Порядок команд: сначала призёры по месту, затем остальные по числу побед, при
 * равенстве — по имени. Соревновательный турнир — результат важнее алфавита.
 */
function sortTeams(teams: RailTeam[]): RailTeam[] {
  return teams.slice().sort((a, b) => {
    const ap = a.placement ?? 0;
    const bp = b.placement ?? 0;
    if (ap !== bp) {
      if (ap === 0) return 1;
      if (bp === 0) return -1;
      return ap - bp;
    }
    const aw = a.stats?.wins ?? 0;
    const bw = b.stats?.wins ?? 0;
    if (aw !== bw) return bw - aw;
    return a.name.localeCompare(b.name, "ru");
  });
}

export function TeamRosterRail({
  teams,
  onSelectPlayer,
  breakdownByTeamId,
}: {
  teams: RailTeam[] | null | undefined;
  /** Поднимает выбор игрока наверх: состоянием владеет страница, а не эта секция. */
  onSelectPlayer?: (userId: string) => void;
  /**
   * KAN-900: разбор команды — чипы стиля, метрики, связки.
   *
   * Ключ — `originalTeamId`, идентификатор КОМАНДЫ ТУРНИРА, а не `id` архивной
   * записи. Метрики реплеев с KAN-909 приходят с тем же идентификатором; `id`
   * не совпадёт ни с чем, и разбор молча пропадёт у всех команд.
   *
   * Приходит ГОТОВЫМ, потому что ярлык стиля считается сравнением с медианой по
   * всем сопоставимым командам турнира. Посчитай его лента — она видит все команды
   * и посчитает верно; посчитай модалка — команда сравнилась бы сама с собой, и
   * чипы исчезли бы у всех разом, без ошибки и без объяснения (KAN-796).
   *
   * Карты нет — имя команды остаётся обычным текстом и никуда не ведёт.
   */
  breakdownByTeamId?: Record<string, TeamBreakdown>;
}) {
  const tUi = useTranslations("Ui");
  const sorted = useMemo(() => sortTeams(teams ?? []), [teams]);
  const [index, setIndex] = useState(0);
  const [openedTeamId, setOpenedTeamId] = useState<string | null>(null);
  const t = useTranslations("TournamentStats.rosterRail");

  const count = sorted.length;

  // Команд стало меньше (сменился турнир) — индекс мог остаться за пределами списка.
  useEffect(() => {
    setIndex((i) => (count === 0 ? 0 : Math.min(i, count - 1)));
  }, [count]);

  const go = useCallback(
    (next: number) => setIndex(Math.max(0, Math.min(count - 1, next))),
    [count],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        go(index - 1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        go(index + 1);
      }
    },
    [go, index],
  );

  if (count === 0) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- карусель: стрелки ловятся на контейнере, фокус при этом на кнопках внутри
    <section
      aria-roledescription={t("carousel")}
      aria-label={t("listLabel")}
      onKeyDown={onKeyDown}
      className="relative"
    >
      <div className="relative overflow-hidden">
        {sorted.map((team, i) => {
          const offset = i - index;
          const depth = Math.abs(offset);
          const active = depth === 0;
          const hidden = depth > STACK_VISIBLE_DEPTH;

          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- клик по соседней карточке дублирует стрелки и точки карусели, с клавиатуры листают ими
            <div
              key={team.originalTeamId}
              aria-hidden={!active}
              // Соседняя карточка кликабельна целиком — это самый естественный
              // способ листать стопку, и он не отменяет стрелок с точками.
              onClick={active ? undefined : () => go(i)}
              className={cn(
                "w-[min(600px,88%)]",
                // Активная — в потоке и задаёт высоту контейнера; остальные поверх.
                // left-1/2 только у абсолютных: у relative он сдвинул бы карточку
                // вправо на половину контейнера вместо центрирования.
                active ? "relative mx-auto" : "absolute left-1/2 top-0",
                "motion-reduce:transition-none",
                !active && !hidden && "cursor-pointer",
                hidden && "pointer-events-none",
              )}
              style={{
                // Своя половина ширины плюс сдвиг по глубине: карточки уходят
                // веером в ту сторону, где они лежат в списке. У активной сдвига
                // нет, и центрируется она обычным mx-auto — трансформ ей ни к чему.
                transform: active
                  ? undefined
                  : `translateX(calc(-50% + ${offset * STACK_SHIFT_PX}px)) scale(${(1 - depth * 0.06).toFixed(3)})`,
                // Масштабируем от верхнего края: иначе сосед с более длинным
                // составом уезжает вниз обеими половинами сразу.
                transformOrigin: "top center",
                opacity: hidden ? 0 : active ? 1 : STACK_NEIGHBOUR_OPACITY,
                filter: active
                  ? "none"
                  : `blur(${(depth * STACK_BLUR_PER_DEPTH).toFixed(1)}px)`,
                boxShadow: active
                  ? "0 26px 64px rgba(0,0,0,.55)"
                  : "0 12px 34px rgba(0,0,0,.4)",
                transition: active ? TRANSITION_ACTIVE : TRANSITION_NEIGHBOUR,
                zIndex: 30 - depth,
                pointerEvents: hidden ? "none" : undefined,
              }}
            >
              {/* inert, а не только pointer-events: `aria-hidden` убирает карточку
                  из озвучки, но Tab по-прежнему заводит в ссылку на твич внутри
                  перекрытой карточки. inert выключает и фокус, и указатель — а
                  клик по самой обёртке (листание) остаётся снаружи, на родителе. */}
              <div inert={!active}>
                <TeamCard
                  team={team}
                  onSelectPlayer={active ? onSelectPlayer : undefined}
                  // Разбор открывает только активная карточка: соседние показаны
                  // размытыми и для чтения не предназначены.
                  onOpenBreakdown={
                    active && breakdownByTeamId?.[team.originalTeamId]
                      ? () => setOpenedTeamId(team.originalTeamId)
                      : undefined
                  }
                />
              </div>
            </div>
          );
        })}
      </div>

      {count > 1 ? (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            disabled={index === 0}
            aria-label={t("prevTeam")}
            className="absolute -left-3.5 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border/80 bg-muted text-foreground shadow-[0_6px_18px_rgba(0,0,0,.35)] transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            disabled={index === count - 1}
            aria-label={t("nextTeam")}
            className="absolute -right-3.5 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border/80 bg-muted text-foreground shadow-[0_6px_18px_rgba(0,0,0,.35)] transition-colors hover:bg-accent disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>

          <div className="mt-0.5 flex justify-center">
            {sorted.map((team, i) => (
              <button
                key={team.originalTeamId}
                type="button"
                onClick={() => go(i)}
                aria-label={tUi("litTeamRosterRail.pokazatSostav", { name: team.name })}
                aria-current={i === index}
                // Зона нажатия 24×24 (WCAG 2.5.8), точка внутри — прежнего размера.
                className="group flex h-6 min-w-6 items-center justify-center px-[3px]"
              >
                <span
                  className={cn(
                    "h-1.5 rounded-full transition-[width,background-color] duration-[250ms] ease-linear",
                    i === index
                      ? "w-[22px] bg-foreground"
                      : "w-1.5 bg-border group-hover:bg-muted-foreground/60",
                  )}
                />
              </button>
            ))}
          </div>
        </>
      ) : null}

      {/* Монтируется ТОЛЬКО когда разбор открыт. Держать её смонтированной впустую
          нельзя: модалка тянет словарь через `useTranslations`, и на страницах без
          провайдера переводов это падение вместо пустого блока. */}
      {openedTeamId && breakdownByTeamId?.[openedTeamId] ? (
        <TeamBreakdownDialog
          breakdown={breakdownByTeamId[openedTeamId]}
          open
          onOpenChange={(next) => {
            if (!next) setOpenedTeamId(null);
          }}
        />
      ) : null}
    </section>
  );
}
